import {
    faChevronLeft,
    faChevronRight,
} from '@awesome.me/kit-eee5f32034/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Paginator } from '@onwardtravelsolutions/package-react/Types';

type NumberedPaginatorProps = {
    paginator: Paginator;
    className?: string;
    ellipses?: string;
};

export default function NumberedPaginator({
    paginator,
    className = '',
    ellipses = '...',
}: NumberedPaginatorProps) {
    const {
        navigateToPreviousPage,
        navigateToNextPage,
        isFirstPage,
        isLastPage,
        currentPage,
        navigateToPage,
    } = paginator;

    const baseButtonClasses =
        'inline-flex relative items-center px-4 py-2 font-semibold text-sm transition';
    const activeButtonClasses = `${baseButtonClasses} z-10 focus:z-20 bg-primary text-gray-700 dark:bg-primary-700 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2 cursor-default`;
    const inactiveButtonClasses = `${baseButtonClasses} focus:z-20 hover:bg-gray-50 dark:hover:bg-neutral-700 focus:outline-offset-0 ring-1 ring-gray-300 dark:ring-gray-600 ring-inset cursor-pointer text-gray-600 dark:text-gray-300 disabled:bg-gray-100 disabled:text-gray-300 dark:disabled:text-gray-700`;
    const arrowBaseButtonClasses = `first:rounded-l-md last:rounded-r-md inline-flex focus:z-20 relative items-center hover:bg-gray-50 dark:hover:bg-neutral-700 disabled:bg-gray-100 dark:disabled:bg-neutral-950 px-2 py-2 focus:outline-offset-0 ring-1 ring-gray-300 dark:ring-gray-600 ring-inset min-w-9 text-gray-400 dark:text-gray-500 disabled:text-gray-300 dark:disabled:text-gray-700 text-center cursor-pointer disabled:cursor-not-allowed`;

    return (
        <nav
            className={`block md:inline-flex isolate -space-x-px md:shadow-xs rounded-md bg-white dark:bg-neutral-900 ${className}`}
            aria-label='Pagination'
        >
            <button
                className={arrowBaseButtonClasses}
                disabled={isFirstPage}
                onClick={() => navigateToPreviousPage()}
                aria-label='Go to previous page'
            >
                <span className='sr-only'>Previous</span>
                <FontAwesomeIcon
                    icon={faChevronLeft}
                    className='m-auto outline-current'
                />
            </button>

            <div className='hidden md:inline-flex'>
                {paginator
                    .getPageNumbers()
                    .map((page: number | string, index) => {
                        if (page === ellipses) {
                            return (
                                <span
                                    key={`ellipses-${index}`}
                                    className={`${inactiveButtonClasses} bg-gray-100 hover:bg-gray-100 dark:bg-neutral-800 dark:hover:bg-neutral-800 cursor-default!`}
                                    aria-hidden='true'
                                >
                                    {ellipses}
                                </span>
                            );
                        }

                        const isActive = page === currentPage;

                        return (
                            <button
                                key={`page-${index}`}
                                className={
                                    isActive
                                        ? activeButtonClasses
                                        : inactiveButtonClasses
                                }
                                onClick={() =>
                                    !isActive &&
                                    typeof page === 'number' &&
                                    navigateToPage(page)
                                }
                                disabled={isActive}
                                aria-current={isActive ? 'page' : undefined}
                                aria-label={`Go to page ${page}`}
                            >
                                {page}
                            </button>
                        );
                    })}
            </div>

            <button
                className={arrowBaseButtonClasses}
                disabled={isLastPage}
                onClick={() => navigateToNextPage()}
                aria-label='Go to next page'
            >
                <span className='sr-only'>Next</span>
                <FontAwesomeIcon icon={faChevronRight} className='m-auto' />
            </button>
        </nav>
    );
}
